import { pick } from 'lodash';
import { ChangeEvent, memo, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { shallow } from 'zustand/shallow';

import {
  AvatarNode,
  JoyajoyImage,
  ListItemProps,
  LoadingSpinner,
  SettingList,
  SvgIcon,
  TextButton,
  TextButtonSize,
  TextButtonVariant,
  TextInput,
} from '..';
import { updateCurrentUser } from '../../../api/Rest/user';
import {
  Regions,
  RegionsCode,
  WorldwideCode,
  getRegionInfo,
} from '../../../constants/region';
import { SVGIconType } from '../../../constants/svgIcon';
import { useShareData } from '../../../context';
import i18n, { TranslationLocale } from '../../../i18n';
import { Regions as TranslatedRegions } from '../../../models/SystemStore/SystemStore';
import { Account, Profile } from '../../../models/UserStore';
import { UserListType } from '../../../models/UserStore/UserStore';
import { useSessionStore } from '../../../stores/SessionStore';
import { getRegionsList } from '../../../stores/SystemStore';
import { useUserStore } from '../../../stores/UserStore';
import { getUiLocale } from '../../../utils/i18n';

import './WEAAccountSettingPages.css';

interface DisplayedNameState {
  displayedName: string;
  isClicked: boolean;
  errorMessage: string;
  isLoading: boolean;
}

interface PasswordState {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
  isClicked: boolean;
  errorMessage: string;
  isLoading: boolean;
}

export const WEASetDisplayNameScreen = memo(() => {
  const navigate = useNavigate();
  const isLoggedIn = useSessionStore((state) => state.isLoggedIn);
  const { t } = useTranslation();
  const userId = useSessionStore((state) => state.activeUserId);

  const { setSharedData } = useShareData();

  useEffect(() => {
    setSharedData(undefined);
  }, [setSharedData]);

  const { name, profilePicture, avatar } = useUserStore((stateUser) => {
    const profile = stateUser.getProfile(userId ?? '');
    return {
      ...pick(profile, ['name', 'profilePicture', 'region']),
    };
  }, shallow);

  const [state, setState] = useState<DisplayedNameState>({
    displayedName: name ?? '',
    isClicked: false,
    errorMessage: '',
    isLoading: false,
  });

  useEffect(() => {
    if (!isLoggedIn()) {
      navigate('/wea-member/start');
    }
  }, [isLoggedIn, navigate]);

  const onDisplayedNameChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setState((s) => ({ ...s, displayedName: event.target.value }));
    },
    [],
  );
  const onSetErrorMessage = useCallback((message: string) => {
    setState((s) => ({ ...s, errorMessage: message }));
  }, []);

  const onClickConfirm = useCallback(() => {
    const handleConfirm = async () => {
      setState((s) => ({ ...s, isClicked: true, isLoading: true }));
      const currentProfile = useUserStore.getState().getProfile(userId ?? '');

      try {
        const response = await updateCurrentUser({
          user: {
            name: state.displayedName,
            region: currentProfile?.region || RegionsCode.US,
          },
        });

        if (response.ok) {
          if (currentProfile) {
            const updatedProfile = new Profile({
              ...currentProfile,
              name: state.displayedName,
            });

            useUserStore
              .getState()
              .setProfiles([updatedProfile], UserListType.Current);
          } else {
            console.error('Current profile not found');
          }

          navigate('/wea-member/account');
        } else {
          onSetErrorMessage('Failed to update display name');
        }
      } catch (error) {
        console.error('Error updating display name:', error);
        onSetErrorMessage('An error occurred while updating display name');
      } finally {
        setState((s) => ({ ...s, isLoading: false }));
      }
    };

    void handleConfirm();
  }, [state.displayedName, userId, navigate, onSetErrorMessage]);

  return (
    <div className="WEAaccount-setting">
      <p className="setting-title">{t('Display Name')}</p>
      {userId && (
        <AvatarNode
          avatar={{
            ...avatar,
            userId,
            source:
              profilePicture && profilePicture.publicUrls?.large
                ? { uri: profilePicture.publicUrls?.small }
                : '',
          }}
          size={250}
        />
      )}
      <div className="centered-container">
        <TextInput
          value={state.displayedName}
          onChange={onDisplayedNameChange}
          // placeholder={t('Displayed name')}
          isClicked={state.isClicked}
        />
        <div className="warning">{t(state.errorMessage)}</div>
      </div>
      <div className="confirm-button">
        <TextButton
          text={t('Confirm')}
          size={TextButtonSize.Medium}
          variant={TextButtonVariant.WEABlue}
          onClick={onClickConfirm}
          disabled={
            state.displayedName.trim().length < 1 ||
            state.displayedName.trim().length > 20
          }
        />
      </div>
      {state.isLoading && <LoadingSpinner loading={state.isLoading} />}
    </div>
  );
});

// separate

export const WEASetUsernameScreen = memo(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isLoggedIn = useSessionStore((state) => state.isLoggedIn);

  const { setSharedData } = useShareData();

  useEffect(() => {
    setSharedData(undefined);
  }, [setSharedData]);

  const userId = useSessionStore((state) => state.activeUserId);
  const [state, setState] = useState({
    newUsername: '',
    currentPassword: '',
    isClicked: false,
    errorMessage: '',
    isLoading: false,
  });

  const { email } = useUserStore((userState) => {
    const account = userState.getAccount(userId ?? '');
    return {
      ...pick(account, ['username', 'email']),
    };
  });

  const listData = [
    {
      title: t('Email'),
      description: email?.address || t('Email not set'),
      onPress: () => void {},
    },
  ];

  const onNewUsernameChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setState((s) => ({ ...s, newUsername: event.target.value }));
    },
    [],
  );

  const onCurrentPasswordChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setState((s) => ({ ...s, currentPassword: event.target.value }));
    },
    [],
  );

  const onSetErrorMessage = useCallback((message: string) => {
    setState((s) => ({ ...s, errorMessage: message }));
  }, []);

  const onClickForgot = useCallback(() => {
    navigate('/wea-member/forgot-password');
  }, [navigate]);

  const onClickConfirm = useCallback(() => {
    const handleConfirm = async () => {
      setState((s) => ({ ...s, isClicked: true, isLoading: true }));
      const currentProfile = useUserStore.getState().getProfile(userId ?? '');
      const currentAccount = useUserStore.getState().getAccount(userId ?? '');

      try {
        const response = await updateCurrentUser({
          user: {
            username: state.newUsername,
            region: currentProfile?.region || RegionsCode.US,
          },
          password: state.currentPassword,
        });

        if (response.ok) {
          if (currentAccount) {
            const updatedAccount = new Account({
              ...currentAccount,
              username: state.newUsername,
            });

            useUserStore
              .getState()
              .setAccounts([updatedAccount], UserListType.Current);
          }

          navigate('/wea-member/account');
        } else {
          onSetErrorMessage(t('Invalid password'));
        }
      } catch (error) {
        console.error('Error updating username:', error);
        onSetErrorMessage('An error occurred while updating username');
      } finally {
        setState((s) => ({ ...s, isLoading: false }));
      }
    };

    void handleConfirm();
  }, [
    userId,
    state.newUsername,
    state.currentPassword,
    navigate,
    onSetErrorMessage,
    t,
  ]);

  useEffect(() => {
    if (!isLoggedIn()) {
      navigate('/wea-member/start');
    }
  }, [isLoggedIn, navigate]);

  return (
    <div className="WEAaccount-setting">
      <p className="setting-title">{t('Username')}</p>
      <div className="centered-container-pw">
        <div className="setting-list">
          <SettingList data={listData} />
        </div>
      </div>
      <div className="centered-container-pw2">
        <TextInput
          value={state.newUsername}
          onChange={onNewUsernameChange}
          placeholder={t('New Username')}
          isClicked={state.isClicked}
        />
        <TextInput
          value={state.currentPassword}
          onChange={onCurrentPasswordChange}
          placeholder={t('Current Password')}
          isClicked={state.isClicked}
          type="password"
        />
        <p
          className="forgot-pw"
          onClick={onClickForgot}
          style={{ color: '#3597E9' }}
        >
          {t('Forgotten Password?')}
        </p>
        <div className="warning">{t(state.errorMessage)}</div>
      </div>
      <div className="confirm-button">
        <TextButton
          text={t('Confirm')}
          size={TextButtonSize.Medium}
          variant={TextButtonVariant.WEABlue}
          onClick={onClickConfirm}
          disabled={
            state.newUsername.length === 0 || state.currentPassword.length === 0
          }
        />
      </div>
      {state.isLoading && <LoadingSpinner loading={state.isLoading} />}
    </div>
  );
});

export const WEASetEmailScreen = memo(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isLoggedIn = useSessionStore((state) => state.isLoggedIn);

  const { setSharedData } = useShareData();

  useEffect(() => {
    setSharedData(undefined);
  }, [setSharedData]);

  const userId = useSessionStore((state) => state.activeUserId);
  const [state, setState] = useState({
    newEmail: '',
    confirmEmail: '',
    currentPassword: '',
    isClicked: false,
    errorMessage: '',
    isLoading: false,
  });

  const { username, email } = useUserStore((userState) => {
    const account = userState.getAccount(userId ?? '');
    return {
      ...pick(account, ['username', 'email']),
    };
  });

  const listData = [
    {
      title: t('Username'),
      description: username || t('error'),
      onPress: () => void {},
    },
    {
      title: t('Email'),
      description: email?.address || t('Email not set'),
      onPress: () => void {},
    },
  ];

  const onNewEmailChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setState((s) => ({ ...s, newEmail: event.target.value }));
    },
    [],
  );

  const onConfirmEmailChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setState((s) => ({ ...s, confirmEmail: event.target.value }));
    },
    [],
  );

  const onCurrentPasswordChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setState((s) => ({ ...s, currentPassword: event.target.value }));
    },
    [],
  );

  const onSetErrorMessage = useCallback((message: string) => {
    setState((s) => ({ ...s, errorMessage: message }));
  }, []);

  const onClickForgot = useCallback(() => {
    navigate('/wea-member/forgot-password');
  }, [navigate]);

  const onClickConfirm = useCallback(() => {
    const handleConfirm = async () => {
      setState((s) => ({ ...s, isClicked: true, isLoading: true }));
      const currentProfile = useUserStore.getState().getProfile(userId ?? '');
      const currentAccount = useUserStore.getState().getAccount(userId ?? '');
      if (state.newEmail !== state.confirmEmail) {
        onSetErrorMessage(t('New emails do not match'));
        setState((s) => ({ ...s, isLoading: false }));
        return;
      }

      try {
        const response = await updateCurrentUser({
          user: {
            email: state.newEmail,
            region: currentProfile?.region || RegionsCode.US,
          },
          password: state.currentPassword,
        });

        if (response.ok) {
          if (currentAccount && email) {
            email.address = state.newEmail;
            const updatedAccount = new Account({
              ...currentAccount,
              email: email,
            });

            useUserStore
              .getState()
              .setAccounts([updatedAccount], UserListType.Current);
          }

          navigate('/wea-member/account');
        } else {
          onSetErrorMessage(t('Invalid password'));
        }
      } catch (error) {
        console.error('Error updating email:', error);
        onSetErrorMessage('An error occurred while updating email');
      } finally {
        setState((s) => ({ ...s, isLoading: false }));
      }
    };

    void handleConfirm();
  }, [
    userId,
    state.newEmail,
    state.confirmEmail,
    state.currentPassword,
    onSetErrorMessage,
    email,
    navigate,
    t,
  ]);

  useEffect(() => {
    if (!isLoggedIn()) {
      navigate('/wea-member/start');
    }
  }, [isLoggedIn, navigate]);

  return (
    <div className="WEAaccount-setting">
      <p className="setting-title">{t('Email')}</p>
      <div className="centered-container-pw">
        <div className="setting-list">
          <SettingList data={listData} />
        </div>
      </div>
      <div className="centered-container-pw2">
        <TextInput
          value={state.newEmail}
          onChange={onNewEmailChange}
          placeholder={t('New Email')}
          isClicked={state.isClicked}
        />
        <TextInput
          value={state.confirmEmail}
          onChange={onConfirmEmailChange}
          placeholder={t('Retype New Email')}
          isClicked={state.isClicked}
        />
        <TextInput
          value={state.currentPassword}
          onChange={onCurrentPasswordChange}
          placeholder={t('Current Password')}
          isClicked={state.isClicked}
          type="password"
        />
        <p
          className="forgot-pw"
          onClick={onClickForgot}
          style={{ color: '#3597E9' }}
        >
          {t('Forgotten Password?')}
        </p>
        <div className="warning">{t(state.errorMessage)}</div>
      </div>
      <div className="confirm-button">
        <TextButton
          text={t('Confirm')}
          size={TextButtonSize.Medium}
          variant={TextButtonVariant.WEABlue}
          onClick={onClickConfirm}
          disabled={
            state.newEmail.length === 0 ||
            state.confirmEmail.length === 0 ||
            state.currentPassword.length === 0
          }
        />
      </div>
      {state.isLoading && <LoadingSpinner loading={state.isLoading} />}
    </div>
  );
});

export const WEASetPasswordScreen = memo(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isLoggedIn = useSessionStore((state) => state.isLoggedIn);

  const { setSharedData } = useShareData();

  useEffect(() => {
    setSharedData(undefined);
  }, [setSharedData]);

  const userId = useSessionStore((state) => state.activeUserId);
  const [state, setState] = useState<PasswordState>({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
    isClicked: false,
    errorMessage: '',
    isLoading: false,
  });

  const { username, email } = useUserStore((userState) => {
    const account = userState.getAccount(userId ?? '');
    return {
      ...pick(account, ['username', 'email']),
    };
  });

  const listData: ListItemProps[] = [
    {
      title: t('Username'),
      description: username || t('error'),
      onPress: () => void {},
    },
    {
      title: t('Email'),
      description: email?.address || t('Email not set'),
      onPress: () => void {},
    },
  ];

  const onCurrentPasswordChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setState((s) => ({ ...s, currentPassword: event.target.value }));
    },
    [],
  );

  const onClickForgot = useCallback(() => {
    navigate('/wea-member/forgot-password');
  }, [navigate]);

  const onNewPasswordChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setState((s) => ({ ...s, newPassword: event.target.value }));
    },
    [],
  );

  const onConfirmPasswordChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setState((s) => ({ ...s, confirmPassword: event.target.value }));
    },
    [],
  );

  const onSetErrorMessage = useCallback((message: string) => {
    setState((s) => ({ ...s, errorMessage: message }));
  }, []);

  const onClickConfirm = useCallback(() => {
    const handleConfirm = async () => {
      setState((s) => ({ ...s, isClicked: true, isLoading: true }));
      const currentProfile = useUserStore.getState().getProfile(userId ?? '');
      if (state.newPassword !== state.confirmPassword) {
        onSetErrorMessage(t('New passwords do not match'));
        setState((s) => ({ ...s, isLoading: false }));
        return;
      }

      try {
        const response = await updateCurrentUser({
          user: {
            password: state.newPassword,
            region: currentProfile?.region || RegionsCode.US,
          },
          password: state.currentPassword,
        });

        if (response.ok) {
          navigate('/wea-member/account');
        } else {
          onSetErrorMessage(t('Invalid password'));
        }
      } catch (error) {
        console.error('Error updating password:', error);
        onSetErrorMessage('An error occurred while updating password');
      } finally {
        setState((s) => ({ ...s, isLoading: false }));
      }
    };

    void handleConfirm();
  }, [
    userId,
    state.newPassword,
    state.confirmPassword,
    state.currentPassword,
    onSetErrorMessage,
    navigate,
    t,
  ]);

  useEffect(() => {
    if (!isLoggedIn()) {
      navigate('/wea-member/start');
    }
  }, [isLoggedIn, navigate]);

  return (
    <div className="WEAaccount-setting">
      <p className="setting-title">{t('Password')}</p>
      <div className="centered-container-pw">
        <div className="setting-list">
          <SettingList data={listData} />
        </div>
      </div>
      <div className="centered-container-pw2">
        <TextInput
          value={state.currentPassword}
          onChange={onCurrentPasswordChange}
          placeholder={t('Current Password')}
          isClicked={state.isClicked}
          type="password"
        />
        <p
          className="forgot-pw"
          onClick={onClickForgot}
          style={{ color: '#3597E9' }}
        >
          {t('Forgotten Password?')}
        </p>
        <TextInput
          value={state.newPassword}
          onChange={onNewPasswordChange}
          placeholder={t('New Password')}
          isClicked={state.isClicked}
          type="password"
        />
        <TextInput
          value={state.confirmPassword}
          onChange={onConfirmPasswordChange}
          placeholder={t('Retype New Password')}
          isClicked={state.isClicked}
          type="password"
        />
        <div className="warning">{t(state.errorMessage)}</div>
      </div>
      <div className="confirm-button">
        <TextButton
          text={t('Confirm')}
          size={TextButtonSize.Medium}
          variant={TextButtonVariant.WEABlue}
          onClick={onClickConfirm}
          disabled={
            state.currentPassword.length === 0 ||
            state.newPassword.length === 0 ||
            state.confirmPassword.length === 0 //||
            // state.newPassword !== state.confirmPassword
          }
        />
      </div>
      {state.isLoading && <LoadingSpinner loading={state.isLoading} />}
    </div>
  );
});

export const WEASetRegionScreen = memo(() => {
  const { t } = useTranslation();
  const activeRegion = useSessionStore((store) => store.activeRegion);
  const position = useSessionStore((store) => store.scrolledPos);

  const { setSharedData } = useShareData();
  const navigate = useNavigate();

  useEffect(() => {
    setSharedData(undefined);
  }, [setSharedData]);

  const userId = useSessionStore((state) => state.activeUserId);
  const userProfile = useUserStore((state) => state.getProfile(userId ?? ''));

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCountry, setSelectedCountry] = useState<RegionsCode>(
    userProfile?.region ?? activeRegion,
  );
  const [filteredRegions, setFilteredRegions] = useState<typeof Regions>([]);
  const [translatedRegions, setTranslatedRegions] = useState<TranslatedRegions>(
    {},
  );
  const [state, setState] = useState({
    isClicked: false,
    errorMessage: '',
    isLoading: false,
  });

  useEffect(() => {
    const fetchTranslatedRegions = async () => {
      // try {
      //   const response = await getRegions({
      //     locale: getUiLocale(i18n.language as TranslationLocale),
      //   });
      //   if (response.ok && response.data) {
      //     const { ...rest } = response.data.regions;
      //     setTranslatedRegions(rest);
      //   }
      // } catch (error) {
      //   console.error('Error fetching translated regions:', error);
      // }

      try {
        const regions = await getRegionsList(
          getUiLocale(i18n.language as TranslationLocale),
        );
        setTranslatedRegions(regions);
      } catch (error) {
        console.error('Failed to fetch regions:', error);
      }
    };

    void fetchTranslatedRegions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  useEffect(() => {
    const filtered = Regions.filter(
      (region) =>
        region.id !== WorldwideCode &&
        region.country &&
        (translatedRegions[region.id]
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
          region.country.toLowerCase().includes(searchTerm.toLowerCase())),
    );

    // Sort the filtered regions
    const sortedRegions = filtered.sort((a, b) => {
      const nameA = translatedRegions[a.id] || a.country || '';
      const nameB = translatedRegions[b.id] || b.country || '';
      return nameA.localeCompare(nameB, i18n.language);
    });

    setFilteredRegions(sortedRegions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, translatedRegions, i18n.language]);

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleSelect = (regionCode: RegionsCode) => {
    setSelectedCountry(regionCode);
  };

  const onSetErrorMessage = useCallback((message: string) => {
    setState((s) => ({ ...s, errorMessage: message }));
  }, []);

  const handleConfirm = useCallback(() => {
    const confirmRegion = async () => {
      if (selectedCountry) {
        useSessionStore.getState().setActiveRegion(selectedCountry);
        if (!userId) {
          history.back();
          return;
        }

        setState((s) => ({ ...s, isClicked: true, isLoading: true }));
        const currentProfile = useUserStore.getState().getProfile(userId ?? '');

        try {
          const response = await updateCurrentUser({
            user: { region: selectedCountry },
          });

          if (response.ok) {
            if (currentProfile) {
              const updatedProfile = new Profile({
                ...currentProfile,
                region: selectedCountry,
              });

              useUserStore
                .getState()
                .setProfiles([updatedProfile], UserListType.Current);
            }

            navigate(-1);
            if (position) {
              const tryScroll = (attempts = 0) => {
                if (attempts > 5) return;
                setTimeout(() => {
                  window.scrollTo(0, position);
                  if (window.scrollY !== position) {
                    tryScroll(attempts + 1);
                  } else {
                    useSessionStore.getState().setScrollPos(null);
                  }
                }, 100);
              };
              tryScroll();
            }
          } else {
            onSetErrorMessage(t('Failed to update region'));
          }
        } catch (error) {
          console.error('Error updating region:', error);
          onSetErrorMessage('An error occurred while updating region');
        } finally {
          setState((s) => ({ ...s, isLoading: false }));
        }
      }
    };

    void confirmRegion();
  }, [selectedCountry, userId, navigate, position, onSetErrorMessage, t]);

  const getRegionImage = (regionId: RegionsCode | typeof WorldwideCode) => {
    if (regionId === WorldwideCode) {
      return Regions.find((r) => r.id === WorldwideCode)?.image || '';
    }
    return getRegionInfo(regionId).image || '';
  };

  return (
    <div className="WEAaccount-setting">
      <p className="setting-title">{t('Country / Region')}</p>
      <div className="centered-container-pw2">
        <div style={{ width: '100%' }}>
          <TextInput
            value={searchTerm}
            onChange={handleSearch}
            placeholder={t('Search countries...')}
            isClicked={state.isClicked}
          />
          <div
            style={{
              height: '400px',
              overflowY: 'auto',
              border: '1px solid #ccc',
            }}
          >
            {filteredRegions.map((region) => (
              <div
                key={region.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '10px',
                  cursor: 'pointer',
                  backgroundColor:
                    selectedCountry === region.id ? '#e0e0e0' : 'transparent',
                }}
                onClick={() => handleSelect(region.id as RegionsCode)}
              >
                <JoyajoyImage
                  resource={{ uri: getRegionImage(region.id) }}
                  style={{ width: 28, height: 28, marginRight: 3 }}
                />
                <span style={{ marginRight: 'auto' }}>
                  {translatedRegions[region.id] || region.country}
                </span>

                {selectedCountry === region.id ? (
                  <SvgIcon
                    name={SVGIconType.CircleTick}
                    fill="#3599E6"
                    stroke="#3599E6"
                    width={28}
                    height={28}
                  />
                ) : (
                  <SvgIcon
                    name={SVGIconType.CircleCheckboxEmpty}
                    stroke={'black'}
                    width={28}
                    height={28}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="warning">{t(state.errorMessage)}</div>
      <div className="confirm-button">
        <TextButton
          text={t('Confirm')}
          size={TextButtonSize.Medium}
          variant={TextButtonVariant.WEABlue}
          onClick={handleConfirm}
          disabled={!selectedCountry}
        />
      </div>
      {state.isLoading && <LoadingSpinner loading={state.isLoading} />}
    </div>
  );
});
